import React, { useState, useEffect } from "react";
import { api } from "../services/authService";
import authService from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { getProfessionColors } from "../utils/professionColors";
import {
  coversProfession,
  displayProfessionsOf,
} from "../utils/professionScope";
import {
  fromProfessions,
  toProfessions,
} from "./common/ProfessionSelect";
import { getInitials, formatRoleName } from "../utils/stringUtils";
import LoadingSpinner from "./common/LoadingSpinner";
import ArchiveUserModal from "./common/ArchiveUserModal";
import ResetLearnerPasswordModal from "./students/ResetLearnerPasswordModal";
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from "./common/Modal";
import InvitationLinksModal from "./common/InvitationLinksModal";

function DeleteUserModal({ user, onClose, onUserDeleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmForceDelete, setConfirmForceDelete] = useState(false);
  const [confirmRegularDelete, setConfirmRegularDelete] = useState(false);
  // What the backend reports the user still owns, shown in the final dialog.
  const [relatedData, setRelatedData] = useState(null);

  const handleForceDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await authService.forceDeleteUser(user.id);
      if (result.success) {
        onUserDeleted();
        onClose();
      } else {
        setError(result.message || "Force delete failed.");
      }
    } catch (err) {
      console.error("❌ Error force deleting user:", err);
      setError("Failed to force delete user.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.deleteUser(user.id);

      if (result.success) {
        onUserDeleted();
        onClose();
      } else if (result.requiresForce) {
        // The user still owns data. Nothing was deleted - the account is
        // untouched until the second confirmation goes through.
        setRelatedData(result.relatedData);
        setConfirmRegularDelete(false);
        setConfirmForceDelete(true);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      setError("Failed to delete user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  // Second confirmation modal for regular delete
  if (confirmRegularDelete) {
    return (
      <Modal
        isOpen
        onClose={() => setConfirmRegularDelete(false)}
        title="Delete this user?"
        subtitle={`${user.firstName} ${user.lastName}`}
        icon="fa-exclamation-triangle"
        tone="danger"
        size="md"
        closeOnBackdrop={!loading}
        footer={
          <>
            <ModalSecondaryButton
              onClick={() => setConfirmRegularDelete(false)}
              disabled={loading}
            >
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              onClick={handleDelete}
              loading={loading}
              loadingText="Deleting..."
              tone="danger"
            >
              <i className="fas fa-trash"></i>
              Delete user
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-cloud">
          This permanently deletes{" "}
          <span className="font-semibold text-grey-dusk">
            {user.firstName} {user.lastName}
          </span>{" "}
          together with everything belonging to them: class memberships,
          enrolments, grades, semester reviews and practice instructor
          assignments.
        </p>
        <p className="mt-3 text-sm font-semibold text-sunrise-red">
          This action cannot be undone.
        </p>
      </Modal>
    );
  }

  if (confirmForceDelete) {
    return (
      <Modal
        isOpen
        onClose={() => {
          setConfirmForceDelete(false);
          setRelatedData(null);
          setError(null);
        }}
        title="Permanently erase this user?"
        subtitle={`${user.firstName} ${user.lastName}`}
        icon="fa-triangle-exclamation"
        tone="danger"
        size="md"
        closeOnBackdrop={!loading}
        footer={
          <>
            <ModalSecondaryButton
              onClick={() => {
                setConfirmForceDelete(false);
                setRelatedData(null);
                setError(null);
              }}
              disabled={loading}
            >
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              onClick={handleForceDelete}
              loading={loading}
              loadingText="Erasing..."
              tone="danger"
            >
              <i className="fas fa-trash"></i>
              Yes, delete everything
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-cloud">
          This erases{" "}
          <span className="font-semibold text-grey-dusk">
            {user.firstName} {user.lastName}
          </span>{" "}
          and everything belonging to them. Afterwards no trace of this user is
          left in the application.
        </p>

        {relatedData && Object.keys(relatedData).length > 0 && (
          <div className="mt-4 text-sm text-grey-dusk bg-sunrise-red/5 border border-sunrise-red/20 rounded-xl p-3">
            <p className="font-semibold text-sunrise-red mb-2">
              The following will be deleted:
            </p>
            <ul className="space-y-1">
              {Object.entries(relatedData).map(([label, count]) => (
                <li key={label} className="flex justify-between gap-4">
                  <span>{label}</span>
                  <span className="font-semibold">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-3 text-sm font-semibold text-sunrise-red">
          This action cannot be undone.
        </p>

        {error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
            {error}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-brand-xl max-w-md w-full">
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center">
              <i className="fas fa-trash-alt mr-3"></i>
              Delete User
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
              disabled={loading}
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3 mt-1"></i>
                <div className="flex-1">
                  <p className="font-semibold mb-1">Cannot Delete User</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
            </div>

            <h3 className="text-lg font-medium text-gray-600 mb-2">
              Are you sure?
            </h3>
            <p className="text-gray-600 mb-4">
              This action will permanently delete the user{" "}
              <strong>
                {user.firstName} {user.lastName}
              </strong>{" "}
              (@{user.username}).
            </p>
            <p className="text-sm text-red-600 font-medium mb-2">
              This action cannot be undone.
            </p>

          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t border-pale-dawn">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="btn-brand-outline"
              onClick={onClose}
              disabled={loading}
            >
              <i className="fas fa-times mr-2"></i>
              Cancel
            </button>
            <button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center"
              onClick={() => setConfirmRegularDelete(true)}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt mr-2"></i>
                  Delete User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserEditModal({ user, onClose, onUserUpdated }) {
  const [editedUser, setEditedUser] = useState({
    firstName: "",
    lastName: "",
    roles: [],
    professionType: "",
    classStartDate: "",
    practiceInstructorDepartment: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const roles = [
    {
      id: 1,
      name: "ADMIN",
      description: "System Administrator",
      color: "sunrise-red",
    },
    {
      id: 2,
      name: "BERUFSBILDNER",
      description: "Training Supervisor",
      color: "grey-dusk",
    },
    {
      id: 3,
      name: "PRACTICE_INSTRUCTOR",
      description: "Instructs apprentices day to day",
      color: "green-cloud",
    },
    {
      id: 4,
      name: "LERNENDE",
      description: "Apprentice in training",
      color: "slate-cloud",
    },
    {
      id: 5,
      name: "REVISOR",
      description: "Final Reviewer",
      color: "green-cloud",
    },
  ];

  const professionTypes = [
    { value: "MEDIAMATIK", label: "Mediamatiker*in" },
    { value: "INFORMATIK", label: "Informatiker*in" },
    { value: "KV", label: "Kaufmann/-frau (KV)" },
    { value: "KUNDENDIALOG", label: "Fachfrau/-mann Kundendialog" },
    { value: "DETAILHANDEL", label: "Detailhandelsfachmann/-frau" },
  ];

  // Only these two describe the same person at once. Every other role is a
  // distinct kind of account and must be held on its own.
  const COMBINABLE_ROLES = ["ADMIN", "BERUFSBILDNER"];

  useEffect(() => {
    if (user) {
      setEditedUser({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        roles: [...(user.roles || [])],
        professionType: user.professionType || "",
        additionalProfessions: fromProfessions(
          user.professionType,
          user.professions,
        ),
        classStartDate: user.classStartDate || "",
        practiceInstructorDepartment: user.practiceInstructorDepartment || "",
      });
    }
  }, [user]);

  const handleRoleToggle = (roleName) => {
    setEditedUser((prev) => {
      const hasRole = prev.roles.some((role) => role.name === roleName);

      if (hasRole) {
        return {
          ...prev,
          roles: prev.roles.filter((role) => role.name !== roleName),
        };
      }

      const role = roles.find((r) => r.name === roleName);
      const picked = { id: role.id, name: role.name };

      // Picking a role that cannot be combined replaces everything else;
      // picking Admin or Berufsbildner keeps only the other combinable one.
      const newRoles = COMBINABLE_ROLES.includes(roleName)
        ? [
            ...prev.roles.filter((r) => COMBINABLE_ROLES.includes(r.name)),
            picked,
          ]
        : [picked];

      return { ...prev, roles: newRoles };
    });
  };

  const handleProfessionChange = (e) => {
    const professionType = e.target.value;
    setEditedUser((prev) => ({
      ...prev,
      professionType,
      // The primary is never also listed as an additional profession.
      additionalProfessions: prev.additionalProfessions.filter(
        (id) => id !== professionType,
      ),
    }));
  };

  const toggleAdditionalProfession = (professionType) => {
    setEditedUser((prev) => ({
      ...prev,
      additionalProfessions: prev.additionalProfessions.includes(professionType)
        ? prev.additionalProfessions.filter((id) => id !== professionType)
        : [...prev.additionalProfessions, professionType],
    }));
  };

  // Which fields a role actually owns. Driven by the roles currently ticked in
  // the dialog, so the form reshapes as soon as a role is added or removed.
  const hasRole = (...names) =>
    editedUser.roles.some((role) => names.includes(role.name));

  const isLearner = hasRole("LERNENDE");
  const isPracticeInstructor = hasRole("PRACTICE_INSTRUCTOR", "PRAXISBILDNER");
  const isBerufsbildner = hasRole("BERUFSBILDNER");
  const isRevisor = hasRole("REVISOR");
  const isAdmin = hasRole("ADMIN");

  // An admin may correct anyone's name, whatever role they hold.
  const showIdentity = true;
  const showProfession = isLearner || isPracticeInstructor || isBerufsbildner;
  // Only instructors supervise apprentices of more than one profession.
  const supervisesLearners = isPracticeInstructor || isBerufsbildner;
  const showDepartment = isPracticeInstructor;
  const showClassStart = isLearner;
  const showApprenticeship = showProfession || showDepartment || showClassStart;

  const profileLabel = isPracticeInstructor
    ? "Practice Instructor"
    : isBerufsbildner
      ? "Berufsbildner"
      : isRevisor
        ? "Revisor"
        : isLearner
          ? "Apprentice"
          : isAdmin
            ? "Admin"
            : "No role";

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const hasPracticeInstructorRole = editedUser.roles.some(
        (role) =>
          role.name === "PRACTICE_INSTRUCTOR" || role.name === "PRAXISBILDNER",
      );
      const hasLearnerRole = editedUser.roles.some(
        (role) => role.name === "LERNENDE",
      );
      const hasBerufsbildnerRole = editedUser.roles.some(
        (role) => role.name === "BERUFSBILDNER",
      );
      // A Berufsbildner is scoped by profession too, so it must not be cleared.
      const needsProfessionType =
        hasLearnerRole || hasPracticeInstructorRole || hasBerufsbildnerRole;

      if (
        hasPracticeInstructorRole &&
        !editedUser.practiceInstructorDepartment.trim()
      ) {
        setError("Practice instructors need a department or team description.");
        setLoading(false);
        return;
      }

      // Update name (first name and last name)
      if (
        editedUser.firstName !== user.firstName ||
        editedUser.lastName !== user.lastName
      ) {
        await api.put(`/users/${user.id}/name`, {
          firstName: editedUser.firstName,
          lastName: editedUser.lastName,
        });
      }

      // Update profession type or class start date
      const normalizedProfessionType = needsProfessionType
        ? editedUser.professionType || null
        : null;
      // Instructors may supervise several professions; learners hold exactly one.
      const normalizedProfessions =
        normalizedProfessionType && supervisesLearners
          ? toProfessions(
              normalizedProfessionType,
              editedUser.additionalProfessions,
            )
          : normalizedProfessionType
            ? [normalizedProfessionType]
            : [];
      const normalizedClassStartDate = hasLearnerRole
        ? editedUser.classStartDate || null
        : null;
      const normalizedPracticeInstructorDepartment = hasPracticeInstructorRole
        ? editedUser.practiceInstructorDepartment || null
        : null;

      const professionsChanged =
        normalizedProfessions.slice().sort().join() !==
        (user.professions || (user.professionType ? [user.professionType] : []))
          .slice()
          .sort()
          .join();

      if (
        normalizedProfessionType !== (user.professionType || null) ||
        professionsChanged ||
        normalizedClassStartDate !== (user.classStartDate || null) ||
        normalizedPracticeInstructorDepartment !==
          (user.practiceInstructorDepartment || null)
      ) {
        await api.put(`/users/${user.id}/profession`, {
          professionType: normalizedProfessionType,
          professions: normalizedProfessions,
          classStartDate: normalizedClassStartDate,
          practiceInstructorDepartment: normalizedPracticeInstructorDepartment,
        });
      }

      // Update roles - always update to ensure consistency
      const roleNames = editedUser.roles.map((role) => role.name);

      await api.put(`/users/${user.id}/roles`, roleNames);

      onUserUpdated();
      onClose();
    } catch (err) {
      console.error("❌ Error updating user:", err);
      console.error("❌ Error details:", err.response?.data);
      setError("Failed to update user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getProfessionBadgeClasses = (profession) => {
    const colors = getProfessionColors(profession);
    return `bg-${colors.light} text-${colors.text} border border-${colors.border}`;
  };

  // Corporate role colors
  const getRoleBadgeClasses = (roleName) => {
    switch (roleName) {
      case "ADMIN":
        return "bg-sunrise-red text-white"; // Corporate primary red
      case "BERUFSBILDNER":
        return "bg-grey-dusk text-white"; // Corporate secondary grey
      case "PRAXISBILDNER":
        return "bg-green-cloud text-white"; // Corporate accent green
      case "LERNENDE":
        return "bg-slate-cloud text-white"; // Corporate tertiary slate
      case "REVISOR":
        return "bg-green-cloud text-white";
      default:
        return "bg-pale-dawn text-grey-dusk"; // Corporate neutral
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit user"
      subtitle={`${user.firstName} ${user.lastName} · @${user.username}`}
      icon="fa-user-edit"
      size="2xl"
      zIndex={70}
      closeOnBackdrop={!loading}
      footer={
        <>
          <ModalSecondaryButton onClick={onClose} disabled={loading}>
            Cancel
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={handleSave}
            loading={loading}
            loadingText="Saving..."
          >
            <i className="fas fa-save"></i>
            Save changes
          </ModalPrimaryButton>
        </>
      }
    >
        <div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                {error}
              </div>
            </div>
          )}

          {/* Top row: avatar + quick info + name inputs */}
          <div
            className={`grid grid-cols-1 gap-4 mb-4 ${
              showIdentity ? "lg:grid-cols-[1fr_1.2fr]" : ""
            }`}
          >
            {/* Compact user card */}
            <div className="bg-ice-white border border-pale-dawn rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-sunrise-red to-sunrise-600 text-white rounded-2xl flex items-center justify-center text-base font-bold">
                  {getInitials(user.firstName, user.lastName, user.username)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-grey-dusk truncate">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    @{user.username}
                  </div>
                  <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-pale-dawn text-[11px] font-semibold text-grey-dusk">
                    {profileLabel}
                  </div>
                </div>
              </div>
            </div>

            {/* Name inputs (compact) */}
            {showIdentity && (
            <div className="bg-white border border-pale-dawn rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-id-card text-sunrise-red mr-2"></i>
                  Identity
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-grey-dusk mb-1">
                    First name
                  </label>
                  <input
                    type="text"
                    className="input-brand w-full"
                    value={editedUser.firstName}
                    onChange={(e) =>
                      setEditedUser((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-grey-dusk mb-1">
                    Last name
                  </label>
                  <input
                    type="text"
                    className="input-brand w-full"
                    value={editedUser.lastName}
                    onChange={(e) =>
                      setEditedUser((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Apprenticeship Details (compact version) */}
          {showApprenticeship && (
          <div className="bg-white border border-pale-dawn rounded-2xl p-4 mb-4">
            <div className="flex items-center justify-between border-b border-pale-dawn pb-3 mb-4">
              <h3 className="text-sm font-bold text-grey-dusk flex items-center">
                <i className="fas fa-briefcase text-sunrise-red mr-2"></i>
                {showClassStart ? "Apprenticeship" : "Assignment"}
              </h3>
              {showClassStart && (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-ice-white text-grey-dusk border border-pale-dawn">
                  <i className="fas fa-calendar-alt mr-2 text-sunrise-red"></i>
                  01.08 fixed
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {/* Profession */}
              {showProfession && (
              <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
                <label className="block text-xs font-semibold text-grey-dusk mb-2">
                  Profession type
                </label>
                <select
                  className="input-brand w-full"
                  value={editedUser.professionType}
                  onChange={handleProfessionChange}
                >
                  <option value="">Select profession type...</option>
                  {professionTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>

                {supervisesLearners && editedUser.professionType && (
                  <div className="mt-3">
                    <span className="block text-xs font-semibold text-grey-dusk mb-1">
                      Additional professions supervised
                    </span>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {professionTypes
                        .filter(
                          (type) => type.value !== editedUser.professionType,
                        )
                        .map((type) => (
                          <label
                            key={type.value}
                            className="flex items-center gap-2 text-xs text-grey-dusk"
                          >
                            <input
                              type="checkbox"
                              checked={editedUser.additionalProfessions.includes(
                                type.value,
                              )}
                              onChange={() =>
                                toggleAdditionalProfession(type.value)
                              }
                            />
                            {type.label}
                          </label>
                        ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      editedUser.professionType
                        ? getProfessionBadgeClasses(editedUser.professionType)
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                  >
                    <i className="fas fa-tag mr-2 text-[10px] opacity-80"></i>
                    {editedUser.professionType || "Not set"}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center">
                    <i className="fas fa-circle-info mr-2 text-sunrise-red"></i>
                    Subjects & templates
                  </span>
                </div>
              </div>
              )}

              {showDepartment && (
              <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
                <label className="block text-xs font-semibold text-grey-dusk mb-2">
                  Department
                </label>
                <input
                  type="text"
                  className="input-brand w-full"
                  value={editedUser.practiceInstructorDepartment}
                  onChange={(e) =>
                    setEditedUser((prev) => ({
                      ...prev,
                      practiceInstructorDepartment: e.target.value,
                    }))
                  }
                  placeholder="e.g. Network Security"
                />
                <div className="mt-3 text-xs text-gray-500 flex items-center">
                  <i className="fas fa-diagram-project mr-2 text-sunrise-red"></i>
                  Shown in timeline and user management for practice instructors
                </div>
              </div>
              )}

              {/* Start year + lehrjahr */}
              {showClassStart && (
              <div className="bg-ice-white border border-pale-dawn rounded-xl p-4">
                <label className="block text-xs font-semibold text-grey-dusk mb-2">
                  Class start year
                </label>

                <div className="bg-white border border-pale-dawn rounded-xl px-3 py-2 flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-[11px] text-gray-500 mb-1">
                      Year
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="4"
                      className="w-full text-base font-bold border-0 p-0 focus:ring-0 text-grey-dusk bg-transparent"
                      placeholder="YYYY"
                      value={
                        editedUser.classStartDate
                          ? editedUser.classStartDate.split("-")[0]
                          : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        setEditedUser({
                          ...editedUser,
                          classStartDate: val ? `${val}-08-01` : "",
                        });
                      }}
                    />
                  </div>

                  <div className="text-gray-300 font-light text-xl">/</div>

                  <div className="opacity-60">
                    <label className="block text-[11px] text-gray-500 mb-1">
                      Month
                    </label>
                    <div className="text-base font-bold text-grey-dusk">08</div>
                  </div>

                  <div className="text-gray-300 font-light text-xl">/</div>

                  <div className="opacity-60">
                    <label className="block text-[11px] text-gray-500 mb-1">
                      Day
                    </label>
                    <div className="text-base font-bold text-grey-dusk">01</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-gray-600 flex items-center">
                    <i className="fas fa-rotate mr-2 text-sunrise-red"></i>
                    Auto-updates on Aug 1
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-sunrise-red border border-sunrise-red/20 shadow-sm">
                    {user.currentLehrjahr
                      ? `${user.currentLehrjahr}. Lehrjahr`
                      : "—"}
                  </span>
                </div>
              </div>
              )}
            </div>
          </div>
          )}

          {/* Roles (compact: tighter spacing + smaller cards) */}
          <div className="bg-white border border-pale-dawn rounded-2xl p-4">
            <div className="flex items-center justify-between border-b border-pale-dawn pb-3 mb-4">
              <h3 className="text-sm font-bold text-grey-dusk flex items-center">
                <i className="fas fa-users-cog text-sunrise-red mr-2"></i>
                Roles & permissions
              </h3>

              <div className="text-xs text-gray-500">
                Selected:{" "}
                <span className="font-semibold text-grey-dusk">
                  {editedUser.roles?.length || 0}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-3 flex items-start gap-2">
              <i className="fas fa-circle-info text-sunrise-red mt-0.5"></i>
              Only Admin and Berufsbildner can be held together. Choosing any
              other role replaces the current selection.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {roles.map((role) => {
                const isChecked = editedUser.roles.some(
                  (userRole) => userRole.name === role.name,
                );
                return (
                  <div
                    key={role.id}
                    className={`border rounded-xl p-3 transition-all ${
                      isChecked
                        ? "border-sunrise-red bg-sunrise-red/5"
                        : "border-pale-dawn hover:border-sunrise-red/30 bg-ice-white/40"
                    }`}
                  >
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 mr-3 w-4 h-4 text-sunrise-red border-gray-300 rounded focus:ring-sunrise-red"
                        checked={isChecked}
                        onChange={() => handleRoleToggle(role.name)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClasses(role.name)}`}
                          >
                            {formatRoleName(role.name)}
                          </span>
                          {isChecked && (
                            <i className="fas fa-check text-green-cloud"></i>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 leading-5">
                          {role.description}
                        </p>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
    </Modal>
  );
}

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterProfession, setFilterProfession] = useState("");
  const [filterLehrjahr, setFilterLehrjahr] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [userToArchive, setUserToArchive] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const { user: currentUser } = useAuth();
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [showInvitationLinksModal, setShowInvitationLinksModal] = useState(false);

  const toggleActionMenu = (userId) => {
    setOpenActionMenuId((prev) => (prev === userId ? null : userId));
  };

  // Corporate role colors - shared function
  const getRoleBadgeClasses = (roleName) => {
    switch (roleName) {
      case "ADMIN":
        return "bg-sunrise-red text-white"; // Corporate primary red
      case "BERUFSBILDNER":
        return "bg-grey-dusk text-white"; // Corporate secondary grey
      case "PRAXISBILDNER":
        return "bg-green-cloud text-white"; // Corporate accent green
      case "LERNENDE":
        return "bg-slate-cloud text-white"; // Corporate tertiary slate
      case "REVISOR":
        return "bg-green-cloud text-white";
      default:
        return "bg-pale-dawn text-grey-dusk"; // Corporate neutral
    }
  };

  const getProfessionBadgeClasses = (profession) => {
    const colors = getProfessionColors(profession);
    return `bg-${colors.light} text-${colors.text} border border-${colors.border}`;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("No authentication token found. Please login again.");
        return;
      }

      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);

      if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Authentication failed. Please login again.");
      } else {
        setError(
          "Failed to load users. Please check if the backend is running.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return currentUser?.roles?.some((role) => role.name === "ADMIN");
  };

  const isPracticeInstructor = () => {
    return currentUser?.roles?.some(
      (role) =>
        role.name === "PRACTICE_INSTRUCTOR" || role.name === "PRAXISBILDNER",
    );
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setUserToResetPassword(user);
    setShowResetPasswordModal(true);
    setError(null);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setError(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setError(null);
  };

  const handleUserUpdated = () => {
    fetchUsers();
    setSuccess("User updated successfully!");
    setTimeout(() => setSuccess(null), 3000);
    handleCloseModal();
  };

  const handleUserDeleted = () => {
    fetchUsers();
    setSuccess("User deleted successfully!");
    setTimeout(() => setSuccess(null), 3000);
    handleCloseDeleteModal();
  };

  const handleResetPasswordSubmit = async () => {
    if (!userToResetPassword) return;

    try {
      setResetPasswordLoading(true);
      setError(null);
      const result = await authService.resetUserPassword(
        userToResetPassword.id,
      );

      if (!result.success) {
        setError(result.message || "Failed to reset user password.");
        return;
      }

      setSuccess(
        `Password for ${userToResetPassword.firstName || userToResetPassword.username} has been reset successfully. New standard password: Welcome2Journey!`,
      );
      setShowResetPasswordModal(false);
      setUserToResetPassword(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error("Error resetting password:", err);
      setError("Failed to reset user password.");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleToggleStatus = (user) => {
    if (user.isArchived) {
      // Direct reactivation is fine
      performStatusToggle(user);
    } else {
      // Require confirmation for archiving
      setUserToArchive(user);
      setShowArchiveModal(true);
    }
  };

  const performStatusToggle = async (user) => {
    try {
      const newArchivedStatus = !user.isArchived;
      await api.put(`/users/${user.id}/status`, {
        isArchived: newArchivedStatus,
      });

      setSuccess(
        newArchivedStatus
          ? `User ${user.firstName} transferred to archive.`
          : `User ${user.firstName} reactivated successfully.`,
      );
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
      setShowArchiveModal(false);
      setUserToArchive(null);
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update user status.");
    }
  };

  const filteredUsers = users.filter((user) => {
    // Archiving Check
    if (showArchived) {
      if (!user.isArchived) return false;
    } else {
      if (user.isArchived) return false;
    }

    const matchesSearch =
      !searchTerm ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole =
      !filterRole || user.roles?.some((role) => role.name === filterRole);

    // A user matches when ANY of their professions matches - filtering by KV
    // must also find the instructor whose primary is Informatik.
    const matchesProfession =
      !filterProfession || coversProfession(user, filterProfession);

    const matchesLehrjahr =
      !filterLehrjahr || String(user.currentLehrjahr) === filterLehrjahr;

    return (
      matchesSearch && matchesRole && matchesProfession && matchesLehrjahr
    );
  });

  if (!isAdmin() && !isPracticeInstructor()) {
    return (
      <div className="min-h-screen bg-ice-white flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-amber-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">Only administrators can manage users.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading users..." />;
  }

  return (
    <div className="min-h-screen bg-ice-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-users-cog text-sunrise-red mr-4"></i>
                  User Management System
                </h1>
                <p className="text-slate-cloud text-lg">
                  Manage users, roles, and profession types
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex items-center gap-3">
                <button
                  onClick={() => setShowInvitationLinksModal(true)}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-pale-dawn text-grey-dusk font-semibold hover:bg-pale-dawn/40 transition-colors"
                >
                  <i className="fas fa-link mr-2 text-sunrise-red"></i>
                  Invitation Links
                </button>
                <span className="px-4 py-2 bg-sunrise-red text-white rounded-full font-semibold flex items-center">
                  <i className="fas fa-shield-alt mr-2"></i>
                  Administrator Access
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                {error}
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-500 mr-3"></i>
                {success}
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="text-green-500 hover:text-green-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card-brand p-6 mb-8">
          <div className="flex flex-col xl:flex-row xl:items-end gap-4">
            {/* Search */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-semibold text-grey-dusk mb-2 uppercase tracking-wide">
                Search Users
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  className="input-brand pl-10"
                  placeholder="Search by name, username or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Role */}
            <div className="w-full sm:w-44 shrink-0">
              <label className="block text-xs font-semibold text-grey-dusk mb-2 uppercase tracking-wide">
                <i className="fas fa-shield-alt text-sunrise-red mr-1.5 text-[10px]"></i>
                Role
              </label>
              <select
                className="input-brand"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="ADMIN">Admin</option>
                <option value="BERUFSBILDNER">Berufsbildner</option>
                <option value="PRACTICE_INSTRUCTOR">Practice Instructor</option>
                <option value="LERNENDE">Apprentice</option>
                <option value="REVISOR">Revisor</option>
              </select>
            </div>

            {/* Profession */}
            <div className="w-full sm:w-52 shrink-0">
              <label className="block text-xs font-semibold text-grey-dusk mb-2 uppercase tracking-wide">
                <i className="fas fa-briefcase text-sunrise-red mr-1.5 text-[10px]"></i>
                Profession
              </label>
              <select
                className="input-brand"
                value={filterProfession}
                onChange={(e) => setFilterProfession(e.target.value)}
              >
                <option value="">All Professions</option>
                <option value="MEDIAMATIK">Mediamatik</option>
                <option value="INFORMATIK">Informatik</option>
                <option value="KV">KV</option>
                <option value="KUNDENDIALOG">Kundendialog</option>
                <option value="DETAILHANDEL">Detailhandel</option>
              </select>
            </div>

            {/* Lehrjahr */}
            <div className="w-full sm:w-40 shrink-0">
              <label className="block text-xs font-semibold text-grey-dusk mb-2 uppercase tracking-wide">
                <i className="fas fa-graduation-cap text-sunrise-red mr-1.5 text-[10px]"></i>
                Lehrjahr
              </label>
              <select
                className="input-brand"
                value={filterLehrjahr}
                onChange={(e) => setFilterLehrjahr(e.target.value)}
              >
                <option value="">All Years</option>
                <option value="1">1. Lehrjahr</option>
                <option value="2">2. Lehrjahr</option>
                <option value="3">3. Lehrjahr</option>
                <option value="4">4. Lehrjahr</option>
              </select>
            </div>
          </div>
        </div>

        {/* User Cards Grid (compact) */}
        <div className="card-brand overflow-hidden bg-white">
          <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold flex items-center">
                <i className="fas fa-user-tag mr-2"></i>
                {showArchived ? "Archived Users" : "Active Users"}
                <span className="ml-2 text-white/80 font-semibold">
                  ({filteredUsers.length})
                </span>
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex bg-white/20 p-1 rounded-lg backdrop-blur-sm">
                  <button
                    onClick={() => setShowArchived(false)}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                      !showArchived
                        ? "bg-white text-sunrise-red shadow-sm"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setShowArchived(true)}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                      showArchived
                        ? "bg-white text-sunrise-red shadow-sm"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    Archived
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 pb-20 md:p-5 md:pb-24">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-ice-white rounded-full flex items-center justify-center mx-auto mb-3 border border-pale-dawn">
                  <i className="fas fa-users text-slate-cloud text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-grey-dusk mb-1">
                  No users found
                </h3>
                <p className="text-xs text-gray-500">
                  Try adjusting your search criteria
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    className={`relative border border-pale-dawn rounded-xl bg-white hover:shadow-brand-lg transition-all duration-200 ${
                      openActionMenuId === u.id ? "z-40" : "z-0"
                    }`}
                  >
                    {/* Top row */}
                    <div className="p-4 pb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-sunrise-red to-sunrise-600 text-white rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                          {getInitials(u.firstName, u.lastName, u.username)}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-grey-dusk truncate text-sm">
                                {u.firstName || u.lastName
                                  ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
                                  : u.username}
                              </h3>
                              <p className="text-xs text-gray-500 truncate">
                                @{u.username}
                              </p>
                            </div>

                            {/* Compact action menu */}
                            <div className="relative shrink-0">
                              <button
                                type="button"
                                onClick={() => toggleActionMenu(u.id)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-pale-dawn bg-white text-gray-500 transition-colors hover:bg-pale-dawn/40 hover:text-grey-dusk"
                                title="User actions"
                                aria-label="User actions"
                              >
                                <i className="fas fa-ellipsis-v text-sm"></i>
                              </button>

                              {openActionMenuId === u.id && (
                                <div className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-2xl border border-pale-dawn bg-white shadow-brand-lg">
                                  <div className="py-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionMenuId(null);
                                        handleEditUser(u);
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-grey-dusk transition-colors hover:bg-pale-dawn/30"
                                    >
                                      <i className="fas fa-pen w-4 text-slate-cloud"></i>
                                      Edit user
                                    </button>

                                    {isAdmin() && currentUser?.id !== u.id && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenActionMenuId(null);
                                          handleResetPassword(u);
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50"
                                      >
                                        <i className="fas fa-key w-4 text-amber-600"></i>
                                        Reset password
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionMenuId(null);
                                        handleToggleStatus(u);
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-grey-dusk transition-colors hover:bg-pale-dawn/30"
                                    >
                                      <i
                                        className={`fas w-4 ${
                                          u.isArchived
                                            ? "fa-box-open text-emerald-600"
                                            : "fa-box-archive text-slate-cloud"
                                        }`}
                                      ></i>
                                      {u.isArchived
                                        ? "Reactivate user"
                                        : "Archive user"}
                                    </button>

                                    <div className="my-2 border-t border-pale-dawn" />

                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (currentUser?.id === u.id) return;
                                        setOpenActionMenuId(null);
                                        handleDeleteUser(u);
                                      }}
                                      disabled={currentUser?.id === u.id}
                                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                      title={
                                        currentUser?.id === u.id
                                          ? "Cannot delete yourself"
                                          : "Delete user"
                                      }
                                    >
                                      <i className="fas fa-trash-alt w-4"></i>
                                      Delete user
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Profession pills inline - one per profession,
                              primary first. Instructors may hold several. */}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {displayProfessionsOf(u).length === 0 ? (
                              <span
                                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-gray-50 text-gray-600 border-gray-200"
                                title="Profession"
                              >
                                <i className="fas fa-briefcase mr-1.5 text-[10px]"></i>
                                Not set
                              </span>
                            ) : (
                              displayProfessionsOf(u).map((profession) => (
                                <span
                                  key={profession}
                                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${getProfessionBadgeClasses(
                                    profession,
                                  )}`}
                                  title={
                                    profession === u.professionType
                                      ? "Primary profession"
                                      : "Additional profession"
                                  }
                                >
                                  <i className="fas fa-briefcase mr-1.5 text-[10px]"></i>
                                  {profession === "ALL"
                                    ? "ALL"
                                    : profession}
                                </span>
                              ))
                            )}

                            {u.currentLehrjahr && (
                              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-sunrise-red/10 text-sunrise-red border border-sunrise-red/20">
                                {u.currentLehrjahr}. Lehrjahr
                              </span>
                            )}

                            {u.practiceInstructorDepartment && (
                              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-grey-dusk border border-pale-dawn">
                                <i className="fas fa-diagram-project mr-1.5 text-[10px] text-sunrise-red"></i>
                                {u.practiceInstructorDepartment}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100" />

                    {/* Roles section (compact) */}
                    <div className="p-4 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] font-semibold text-grey-dusk flex items-center">
                          <i className="fas fa-shield-alt mr-1.5 text-sunrise-red text-[10px]"></i>
                          Roles
                        </p>
                        <span className="text-[11px] text-gray-500">
                          {u.roles?.length || 0} assigned
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {u.roles && u.roles.length > 0 ? (
                          u.roles.map((role) => (
                            <span
                              key={role.id}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${getRoleBadgeClasses(
                                role.name,
                              )}`}
                            >
                              {formatRoleName(role.name)}
                            </span>
                          ))
                        ) : (
                          <span className="px-2.5 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-full text-[11px] font-semibold">
                            No roles
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Edit Modal */}
        {showEditModal && selectedUser && (
          <UserEditModal
            user={selectedUser}
            onClose={handleCloseModal}
            onUserUpdated={handleUserUpdated}
          />
        )}

        {/* Delete Modal */}
        {showDeleteModal && userToDelete && (
          <DeleteUserModal
            user={userToDelete}
            onClose={handleCloseDeleteModal}
            onUserDeleted={handleUserDeleted}
          />
        )}

        {/* Archive Confirmation Modal */}
        {showArchiveModal && userToArchive && (
          <ArchiveUserModal
            user={userToArchive}
            onClose={() => {
              setShowArchiveModal(false);
              setUserToArchive(null);
            }}
            onConfirm={() => performStatusToggle(userToArchive)}
          />
        )}

        <ResetLearnerPasswordModal
          isOpen={showResetPasswordModal}
          user={userToResetPassword}
          onClose={() => {
            setShowResetPasswordModal(false);
            setUserToResetPassword(null);
          }}
          onConfirm={handleResetPasswordSubmit}
          isLoading={resetPasswordLoading}
        />

        <InvitationLinksModal
          isOpen={showInvitationLinksModal}
          onClose={() => setShowInvitationLinksModal(false)}
        />
      </div>
    </div>
  );
}

export default UserList;
